<template>
  <print-cloud-top-navigation @showOrderModal="showOrderModal">
  </print-cloud-top-navigation>
  <div class="row">
    <div class="configurator-info-block blog-asside-right col-md-3">
      <div class="logo-block">
        <a class="" href=""><img :src="'/images/exhibitions/' + exhibitionImage" alt=""></a>
      </div>
      <div class="configurator-info-content">
        <configurator-exhibition
          class="hidden-xs hidden-sm"
          :exhibition="request.exhibition">
        </configurator-exhibition>

        <print-cloud-request :request="request"></print-cloud-request>

        <print-cloud-basket v-if="isCloudStatusOpen && !isShopClosed"
          :country="request.country"
          :exhibition="request.exhibition">
        </print-cloud-basket>

				<PdfDocuments />

        <configurator-contact v-if="request.exhibition.contact"
          class="hidden-xs hidden-sm"
          :contact="request.exhibition.contact">
        </configurator-contact>
      </div>
    </div>
    <div class="configurator-content-block blog-lst col-md-9 p1">
      <div class="clearfix">
        <h2 class="mt-4">{{ $t('configurator.headings.print_cloud') }}</h2>
        <div v-if="cloud.status >= PRINT_CLOUD_STATUS_USER_READY" class="alert alert-success" role="alert">
          {{ $t('configurator.alerts.success.files_are_being_processed') }}
        </div>

        <div v-if="isShopClosed && cloud.status === PRINT_CLOUD_STATUS_OPEN" role="alert" class="alert alert-info">
          {{ closedMessage }}
        </div>
        <p>{{ $t('configurator.descriptions.print_cloud_introduction') }}</p>
        <!--<p>Wenn Sie zusätzliche Drucke bestellen wollen, dann ...</p>-->

        <template v-if="booth">
          <print-cloud-booth :booth="booth" :booth-type="request.booth_type"></print-cloud-booth>
        </template>
        
        <template v-for="article in printableArticles" :key="article.id">
          <print-cloud-article :article="article"></print-cloud-article>
        </template>

        <div class="mt-3">
          <div v-if="!allPrintsHaveUploads" role="alert" class="alert alert-danger">
            {{ $t('configurator.descriptions.upload_files_for_every_print') }}
          </div>
          <p v-if="allPrintsHaveUploads && cloud.status === PRINT_CLOUD_STATUS_OPEN">
            {{ $t('configurator.descriptions.all_required_files_uploaded') }}
          </p>
          <div class="text-center">
            <button v-if="cloud.status === PRINT_CLOUD_STATUS_OPEN && articleCount <= 0"
              type="button"
              class="btn btn-primary margin-auto"
              :disabled="!allPrintsHaveUploads"
              @click="markAsReady">
              {{ $t('configurator.buttons.mark_as_ready') }}
            </button>
          </div>
          <div class="text-center">
            <button v-if="articleCount > 0"
                    type="button"
                    class="btn btn-primary margin-auto"
                    @click="showOrderModal">
              {{ $t('configurator.buttons.mark_as_order') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <print-cloud-order-modal
    :is-active="orderModalActive"
    :countries="countries"
    :form="submitForm"
    :request="request"
    @close="hideOrderModal"
    @submit="submitOrder">
  </print-cloud-order-modal>
  <app-footer></app-footer>
</template>

<script setup>
import moment from 'moment'
import { computed, onMounted, ref, reactive, inject } from 'vue'
import { useMainStore } from '@/store'
import { useCloudStore } from '@/stores/cloud'
import PrintCloud from '@/classes/PrintCloud'
import { PRINT_CLOUD_STATUS_USER_READY, PRINT_CLOUD_STATUS_OPEN, ARTICLE_TYPE_PRINTABLE } from '@/constants'
import { showLoadingIndicator, showSuccessToast, showErrorToast } from '@/utils/toast'
import CloudBasket from '@/classes/CloudBasket'
import { useI18n } from 'vue-i18n'
import Form from '@/classes/Form'
import _ from 'lodash'
import Logger from '@/services/logging'
import PdfDocuments from '@/components_new/Configurator/Panels/PdfDocuments.vue'

const { t } = useI18n({ useScope: 'global' })
const store = useMainStore()
const cloudStore = useCloudStore()
/** @type {Logger} */
const logger = inject('logger')
// define passed props
const props = defineProps({
  articles: {
    type: Array,
    default: []
  },
  booth: {
    type: Object,
    required: false
  },
  boothArticles: {
    type: Array,
    default: [],
  },
  printCloud: {
    type: Object,
    required: true,
  },
  request: {
    type: Object,
    required: true,
  },
  countries: {
    type: Array,
    default: [],
  },
  printingInformation: {
    type: Array,
    default: []
  },
})
// define reactive props
const orderModalActive = ref(false)
const submitForm = reactive(new Form({
  use_order_number: false,
  order_number: null,
  comment: '',
  articles: [],
  print_configurations: [],
  booth_configurations: [],
  has_bill_address: false,
  bill_company: null,
  bill_name: null,
  bill_street_name: null,
  bill_street_number: null,
  bill_place: null,
  bill_zip: null,
  bill_country_id: null,
  bill_email: null,
  bill_phone: null,
  bill_vat_id: null,
  bill_vat_id_prefix: '',
  bill_country_legal_form_id: null,
  price_level_id: null,
}))
// define computed props
const locale = computed(() => store.settings.locale)
const cloud = computed(() => store.tmp.cloud)
const basket = computed(() => store.print.basket)
const printArticles = computed(() => cloudStore.data.print_articles)
const activePriceLevel = computed(() => store.activePriceLevel)

const inEnglish = computed(() => {
  return locale.value === 'en'
})

const isCloudStatusOpen = computed(() => {
  return cloud.value.status === PRINT_CLOUD_STATUS_OPEN
})

const closedMessage = computed(() => {
	return (inEnglish.value)
		? props.request.exhibition.closed_message_en
		: props.request.exhibition.closed_message
})

const exhibitionImage = computed(() => {
	if (props.request.exhibition.logo !== null && props.request.exhibition.logo != "") {
		return props.request.exhibition.logo
	}

	return "default_configurator_logo.png"
})

const isShopClosed = computed(() => {
	if (props.request.exhibition.closed_date === null) {
		return false
	}
	let now = moment()
	let closed = moment(props.request.exhibition.closed_date)

	return (now.diff(closed) >= 0)
})

const allPrintsHaveUploads = computed(() => {
  let printsBought = 0
  let printsUploaded = 0

  // count configurations for articles
  const prints = printableArticles.value.map((item) => (item.prints))

  try {
    const configurations = prints.flat()
    .map((item) => item.configurations)
    .flat()

    configurations.forEach((configuration) => {
      printsBought++
      if (configuration.upload) {
        printsUploaded++
      }
    }) 
  } catch (error) {
    logger.error(
      'allPrintsHaveUploads computed',
      JSON.stringify(error),
      JSON.stringify(printableArticles.value)
    )
  }

  // count configurations for booth
  const orderedRefinements = printArticles.value.filter((printArticle) => {
    return printArticle.booth_refinement_id !== null
  })
  const groupWithUpload = []
  orderedRefinements.forEach((refinement) => {
    if (refinement.configurations.length > 0 && refinement.configurations[0].is_logo_print) {
      printsBought++
      if (refinement.configurations[0].upload) {
        printsUploaded++
        groupWithUpload.push(refinement.configurations[0].group)
      } else if (groupWithUpload.includes(refinement.configurations[0].group)) {
        // refinements which are part of a surface overlapping print
        printsUploaded++
      }
    }
  })

  return printsBought === printsUploaded
})

const printableArticles = computed(() => {
  return printArticles.value.filter(item => item.article.type === ARTICLE_TYPE_PRINTABLE)
})
// define methods
const markAsReady = (async () => {
  showLoadingIndicator(t('configurator.indicators.mark_as_user_ready'))
  await axios.post('/print/' + props.printCloud.uuid + '/ready')
    .then(response => {
      store.tmp.cloud.status = PRINT_CLOUD_STATUS_USER_READY
      store.print.basket.clear()
      showSuccessToast(t('configurator.toasts.messages.mark_as_ready'), t('toast.title.success'))
    })
    .catch(err => {
      console.error(err.message)
      showErrorToast(t('configurator.toasts.errors.mark_as_ready'), t('toast.title.error'))
    })
})

const calculateDaysToGo = () => {
	let now = moment()
	let start = moment(props.request.exhibition.from)

	return start.diff(now, 'days')
}

const articleCount = computed(() => {
  if (basket.value.print_items.length === 0) {
    return 0
  }
  return basket.value.print_items.reduce((a, item) => {
    return a + item.count
  }, 0)
})

const submitOrder = async() => {
  showLoadingIndicator(
    t('configurator.indicators.submit_order'), 
    t('configurator.indicators.one_moment_please')
  )
  submitForm.articles = basket.value.print_items
  submitForm.print_configurations = basket.value.print_configurations
  submitForm.booth_configurations = basket.value.booth_configurations
  submitForm.price_level_id = (activePriceLevel.value) ? activePriceLevel.value.id : null
  await submitForm.post('/print/' + cloud.value.uuid + '/order-prints')
    .then((response) => {
      store.print.basket.clear()
      hideOrderModal()
      cloudStore.data.print_articles = response.data.articles
      store.tmp.booth = response.data.booth
      showSuccessToast(t('success.message', 'success.title'), t('toast.title.success'))
    })
    .catch(err => {
      console.error(err)
      showErrorToast(t('errors.please_try_again', 'errors.occurred'), t('toast.title.error'))
    })
}

const showOrderModal = () => {
  orderModalActive.value = true
}

const hideOrderModal = () => {
  orderModalActive.value = false
}

onMounted(async () => {
  store.tmp.cloud = new PrintCloud(props.printCloud)
	store.tmp.days_to_go = calculateDaysToGo()
	store.tmp.is_exhibition_closed = isShopClosed.value
	store.tmp.exhibition_slug = props.request.exhibition.slug
  store.tmp.booth_draft = props.request.booth_draft
  store.tmp.printing_information = props.printingInformation
  store.tmp.characters_inclusive = props.request.exhibition.characters_inclusive
	// This mode hides late delivery charge and changes the calculation.
	// It's necessary because Lueco used the standard prices for early bird sale
	// and misused the late delivery charge for standard prices
	store.tmp.early_bird_mode = [36, 37].includes(props.request.exhibition.id)
	store.tmp.late_delivery = props.request.exhibition.late_delivery

  if (store.print.basket.id !== cloud.value.uuid) {
    store.print.basket = new CloudBasket({ 
      id: cloud.value.uuid,
      width: props.request.booth_width,
      depth: props.request.booth_depth,
      net_value_only: !props.request.country.vat_display_required,
      late_delivery: props.request.exhibition.late_delivery,
      exhibition_start_date: props.request.exhibition.from,
      early_bird_mode: (props.request.exhibition.id === 36),
    })
  }

  store.tmp.booth_articles = _.cloneDeep(props.boothArticles)
  store.tmp.booth = _.cloneDeep(props.booth)
  if (props.booth && props.booth.files) {
    store.tmp.booth_package_documents = Object.values(props.booth.files)
  }

  cloudStore.data.print_articles = _.cloneDeep(props.articles)
  cloudStore.data.booth = _.cloneDeep(props.booth)
  cloudStore.data.booth_type = props.request.booth_type
  cloudStore.data.cloud = store.tmp.cloud
  cloudStore.data.exhibition_prices = props.request.exhibition
    .articles
    .map((article) => {
      const prices = JSON.parse(article.pivot.prices)
      return { id: article.id, price_net: prices.price_net, vat_rate: prices.price_vat_rate }
    })
    .reduce((a,b) => (a[b.id]=b, a), {})
  store.fetchAvailability()
  await store.fetchExhibitionInformation()
    .then(() => {
      store.print.basket.price_level = activePriceLevel.value
      store.print.basket.updateTotals()
    })
})
</script>